import { useState } from 'react';
import { t } from 'i18next';

import PageLayout from '@/components/Layout/PageLayout';
import type { DateRange } from '@/components/molecules/DateRangePicker/DateRangePicker';
import DownloadOptions from '@/components/molecules/ExportDataDropDown/DownloadOptions';
import { ExportDataDropDown } from '@/components/molecules/ExportDataDropDown/ExportDataDropDown';
import SegmentedControl from '@/components/molecules/SegmentedControl';
import { usePermissions } from '@/contexts/auth';
import { useCage, useCageName } from '@/contexts/cage';
import useCageFilter from '@/contexts/cage-filter-context';
import { selectMeasurementSource, useGetCageDailyLice, useGetCageWeeklyLice } from '@/domain/lice';
import { useQueryParams } from '@/hooks/useQueryParams';
import type { CagePageInterface } from '@/routes/Client/Cage/utils/cage-pages-interface';
import { dayToString, toDateString } from '@/utils/date';
import { isChileanLiceType } from '@/utils/locationTypeHelpers';

import { buildLiceDownload } from '../liceDownload';

import CageLiceGraphsPageContent from './CageLiceGraphsPageContent';

import styles from './CageLiceGrahps.module.scss';

export default function CageLiceGraphsContainer({ cageId }: CagePageInterface) {
    const { client, stock } = useCage();
    const cageName = useCageName();
    const { isOptoScaleAdmin } = usePermissions();

    const { from, to, daypartTag, setFilter, selectedFishGroupId } = useCageFilter();

    const defaultMeasurmentSource = client?.featureFlags?.includes('LICE_LEGACY_VIEW')
        ? 'raw'
        : 'legacy';
    const isChileanLice = isChileanLiceType(stock?.liceType);
    const showToggle = isOptoScaleAdmin ? true : !isChileanLice;

    const [measurementSource, setMeasurementSource] = useState<'raw' | 'legacy'>(
        defaultMeasurmentSource
    );

    const dayFrom = from ? dayToString(from) : undefined;
    const dayTo = to ? dayToString(to) : undefined;

    const queryParams = useQueryParams();

    const dailyLice = useGetCageDailyLice(
        cageId,
        { dayFrom, dayTo, dayPart: daypartTag, fishGroupId: selectedFishGroupId },
        { staleTime: 5 * 60000 }
    );
    const weeklyLice = useGetCageWeeklyLice(
        cageId,
        { dayFrom, dayTo, dayPart: daypartTag, fishGroupId: selectedFishGroupId },
        { staleTime: 5 * 60000 }
    );

    const isLoading = dailyLice.isLoading || weeklyLice.isLoading;
    const error = dailyLice.error || weeklyLice.error;

    const liceDownload = buildLiceDownload({
        rows: dailyLice.data,
        daypart: queryParams[0]?.d,
        t,
        isChileanLice,
    });

    const handleSetCageFilter = (dateReturn: DateRange) => {
        setFilter({
            from: dateReturn.dateFrom,
            to: dateReturn.dateTo,
        });
    };

    return (
        <PageLayout
            title={cageName}
            subtitle={t('cageLice.cageLiceGraphs.subtitle')}
            width="wide"
            actions={
                <div className={styles.pageTitleRowButtonContainer}>
                    <ExportDataDropDown>
                        <DownloadOptions
                            data={liceDownload.data}
                            columns={liceDownload.columns}
                            nameToInclude={liceDownload.nameToInclude}
                        />
                    </ExportDataDropDown>
                    <div>
                        {showToggle && (
                            <SegmentedControl<'raw' | 'legacy'>
                                defaultValue={measurementSource}
                                onChange={(value) => setMeasurementSource(value)}
                                options={[
                                    {
                                        value: 'raw',
                                        label: t('common.labels.rawData'),
                                        tooltip: t('common.labels.rawdataexplain'),
                                    },
                                    {
                                        value: 'legacy',
                                        label: t('common.labels.simulatedManualCount'),
                                        tooltip: t('common.labels.reportdataexplain'),
                                    },
                                ]}
                            />
                        )}
                    </div>
                </div>
            }>
            <CageLiceGraphsPageContent
                liceCount={dailyLice.data.map((row) =>
                    selectMeasurementSource(row, measurementSource)
                )}
                weeklyLice={weeklyLice.data.map((row) =>
                    selectMeasurementSource(row, measurementSource)
                )}
                cageFilterHandler={handleSetCageFilter}
                inputDateObject={{
                    dateFrom: toDateString(from),
                    dateTo: toDateString(to),
                }}
                isChileanLice={isChileanLice}
                isLoading={isLoading}
                error={error}
            />
        </PageLayout>
    );
}
